import {
    createCampaign,
    deleteCampaign as deleteCampaignCall,
    requestCampaignReview,
} from '@/api/campaignPlaner';
import { TreeNodeLike } from '@/components/TreeSelect/useTreeSelect/types';
import { deduplicateCustomerRules } from '@/dashboards/CampaignPlanner/helpers/deduplicateCustomerRules';
import { useCampaignPlannerUrl } from '@/dashboards/CampaignPlanner/hooks/useCampaignPlannerUrl';
import { Campaign } from '@/dashboards/CampaignPlanner/types';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { OptimisationBase } from '@/optimizer';
import { withTimeout } from '@/optimizer/helpers/withTimeout';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useMemo, useState } from 'react';
import { createContext } from 'use-context-selector';

// The optimizer runs Pyodide on a single worker thread. If that thread is busy
// (or a worker message is dropped) the active-days lookup can hang
// indefinitely, which would otherwise leave the save action stuck. Bound the
// wait so we save without the optional active-days metadata instead.
const OPTIMIZER_LOOKUP_TIMEOUT_MS = 10_000;

interface SaveCampaignProps {
    projectId: string;
    name: string;
    startDate: string | undefined;
    endDate: string | undefined;
    budget: number | undefined;
    optimizationBase?: OptimisationBase;
    disabledChannels?: string[];
}

interface SendForReviewProps {
    comment: string;
}

interface CampaignPlannerContextValue {
    showSaveCampaignDialog: boolean;
    setShowSaveCampaignDialog: (value: boolean) => void;
    saveCampaign: (data: SaveCampaignProps) => Promise<void>;
    isSavingCampaign: boolean;
    requestCampaign: Campaign | null;
    setRequestCampaign: (value: Campaign | null) => void;
    saveAsCampaignReference: Campaign | null;
    setSaveAsCampaignReference: (value: Campaign | null) => void;
    deleteCandidate: string | null;
    setDeleteCandidate: (value: string | null) => void;
    sendForReview: (data: SendForReviewProps) => Promise<void>;
    deleteCampaign: () => Promise<void>;
    medias: TreeNodeLike[];
}

export const CampaignPlannerContext =
    createContext<CampaignPlannerContextValue>({
        showSaveCampaignDialog: false,
        setShowSaveCampaignDialog: (_: boolean) => {},
        saveCampaign: async (_: SaveCampaignProps) => {},
        isSavingCampaign: false,
        requestCampaign: null,
        deleteCandidate: null,
        saveAsCampaignReference: null,
        setRequestCampaign: () => {},
        setSaveAsCampaignReference: () => {},
        setDeleteCandidate: () => {},
        sendForReview: async (_: SendForReviewProps) => {},
        deleteCampaign: async () => {},
        medias: [],
    });

export const CampaignPlannerContextProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const queryClient = useQueryClient();
    const {
        params: { projectId },
    } = useCampaignPlannerUrl();

    const {
        variableNameMap,
        dashboard: { id: dashboardId },
    } = useDashboardGlobalContext();
    const {
        medias: optimizerMedias,
        getActiveDaysPerMedium,
        optimizerReady,
    } = useOptimizer();
    const medias: TreeNodeLike[] = useMemo(
        () =>
            (optimizerMedias || []).map((media: string) => ({
                id: media,
                label: variableNameMap[media] || media,
            })),
        [optimizerMedias, variableNameMap],
    );

    const [showSaveCampaignDialog, setShowSaveCampaignDialog] =
        useState<boolean>(false);
    const [requestCampaign, setRequestCampaign] = useState<Campaign | null>(
        null,
    );
    const [saveAsCampaignReference, setSaveAsCampaignReference] =
        useState<Campaign | null>(null);
    const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);
    const [isSavingCampaign, setIsSavingCampaign] = useState<boolean>(false);

    const resolveActiveDaysPerMedium = async (): Promise<
        Record<string, number> | undefined
    > => {
        if (!optimizerReady) {
            return undefined;
        }

        try {
            return await withTimeout(
                getActiveDaysPerMedium(),
                OPTIMIZER_LOOKUP_TIMEOUT_MS,
                'Active days per medium lookup',
            );
        } catch (error) {
            // A timeout or worker failure here is non-fatal: the active-days
            // metadata is optional, so save the campaign without it instead of
            // blocking the user.
            // eslint-disable-next-line no-console
            console.error('Failed to fetch active days per medium:', error);
            return undefined;
        }
    };

    const saveCampaign = async ({
        disabledChannels,
        ...data
    }: SaveCampaignProps) => {
        setIsSavingCampaign(true);
        try {
            const customerRules = saveAsCampaignReference
                ? saveAsCampaignReference.customerRules
                : [];

            const uniqueCustomerRules =
                deduplicateCustomerRules(customerRules);

            const activeDaysPerMedium = await resolveActiveDaysPerMedium();

            await createCampaign({
                ...data,
                dashboardId,
                rules: uniqueCustomerRules,
                disabledChannels,
                sourceCampaignId: saveAsCampaignReference?.id,
                activeDaysPerMedium,
            });
            await queryClient.invalidateQueries({
                queryKey: ['savedCampaigns'],
            });
            await queryClient.invalidateQueries({
                queryKey: ['savedCampaign'],
            });
            setShowSaveCampaignDialog(false);
            setSaveAsCampaignReference(null);
        } finally {
            setIsSavingCampaign(false);
        }
    };

    const sendForReview = async ({ comment }: SendForReviewProps) => {
        if (!requestCampaign) return;

        await requestCampaignReview({
            comment,
            campaignId: requestCampaign.id,
            projectId,
        });

        await queryClient.invalidateQueries({ queryKey: ['savedCampaigns'] });
        await queryClient.invalidateQueries({ queryKey: ['savedCampaign'] });
        setRequestCampaign(null);
    };

    const deleteCampaign = async () => {
        if (!deleteCandidate) return;

        await deleteCampaignCall({
            campaignId: deleteCandidate,
            projectId,
        });

        await queryClient.invalidateQueries({ queryKey: ['savedCampaigns'] });
        await queryClient.invalidateQueries({ queryKey: ['savedCampaign'] });

        setDeleteCandidate(null);
    };

    return (
        <CampaignPlannerContext.Provider
            value={{
                showSaveCampaignDialog,
                setShowSaveCampaignDialog,
                saveCampaign,
                isSavingCampaign,
                requestCampaign,
                setRequestCampaign,
                setDeleteCandidate,
                deleteCandidate,
                setSaveAsCampaignReference,
                saveAsCampaignReference,
                sendForReview,
                deleteCampaign,
                medias,
            }}
        >
            {children}
        </CampaignPlannerContext.Provider>
    );
};
